import { useState, useEffect } from "react";

const C = {
  red: "#E01A2E", redSoft: "#FCD5D8", navy: "#0B1E3B",
  body: "#F7F5F0", slate: "#5B6B7E", amber: "#F5A623",
  green: "#1E9E63", ink: "#1A1E24"
};

const TABS = [
  { id: "ops", label: "1. Ops Console" },
  { id: "home", label: "2. Consumer PWA Home" },
  { id: "sos", label: "3. Emergency Active" },
  { id: "install", label: "4. Install Walkthrough" },
];

export default function HeroPreviewPage() {
  const [tab, setTab] = useState("ops");
  return (
    <div style={{ background: C.body, minHeight: "100vh", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ background: C.navy, color: "#fff", padding: "14px 22px", display: "flex", alignItems: "center", gap: 18 }}
           data-testid="hero-preview-header">
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700, letterSpacing: "-0.02em" }}>
          <img src="/resq1-logo.webp" alt="ResQ1" style={{ height: 32, filter: "brightness(0) invert(1)" }} />
          <span style={{ opacity: 0.55, fontWeight: 500, marginLeft: 6 }}>· Design preview v1.8</span>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              style={{
                padding: "8px 14px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.12)",
                background: tab === t.id ? C.red : "transparent", color: "#fff", cursor: "pointer",
                fontSize: 13, fontWeight: 500, letterSpacing: "-0.01em"
              }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ padding: 24 }}>
        {tab === "ops" && <OpsConsole />}
        {tab === "home" && <PWAHome />}
        {tab === "sos" && <EmergencyActive />}
        {tab === "install" && <InstallWalkthrough />}
      </div>
    </div>
  );
}

/* ---------- 1. OPS CONSOLE ---------- */
function OpsConsole() {
  const [tick, setTick] = useState(154);
  useEffect(() => { const id = setInterval(() => setTick(t => t + 1), 1000); return () => clearInterval(id); }, []);
  const fmt = s => `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
  return (
    <div style={{ position: "relative", height: "calc(100vh - 130px)", borderRadius: 12, overflow: "hidden",
                  background: `linear-gradient(135deg, #1a2332 0%, #0f1720 100%)`, border: `1px solid ${C.navy}` }}
         data-testid="hero-ops-console">
      {/* Simulated map grid */}
      <svg width="100%" height="100%" style={{ position: "absolute", inset: 0, opacity: 0.15 }}>
        <defs><pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#5B6B7E" strokeWidth="0.5" />
        </pattern></defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>
      {/* Panic pin */}
      <div style={{ position: "absolute", top: "42%", left: "58%" }}>
        <div style={{ width: 20, height: 20, background: C.red, borderRadius: "50% 50% 50% 0", transform: "rotate(-45deg)", boxShadow: `0 0 0 8px ${C.red}44` }} />
      </div>
      {/* Top-left counters */}
      <div style={panel({ top: 20, left: 20, padding: 18, minWidth: 340 })} data-testid="ops-counters">
        <div style={{ fontSize: 11, color: C.slate, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Live Ops · today</div>
        <div style={{ display: "flex", gap: 24 }}>
          <Stat n="3" label="Active" color={C.red} />
          <Stat n="12" label="Today" color={C.navy} />
          <Stat n="2m 14s" label="Avg response" color={C.green} mono />
        </div>
      </div>
      {/* Cmd-K hint */}
      <div style={{ ...panel({ top: 20, right: 20, padding: "10px 14px" }), display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}
           data-testid="ops-cmdk-hint">
        <span style={{ color: C.slate }}>Search anywhere</span>
        <kbd style={kbd}>⌘K</kbd>
      </div>
      {/* Left request cards */}
      <div style={{ position: "absolute", top: 130, left: 20, display: "flex", flexDirection: "column", gap: 10, width: 340 }}>
        <RequestCard name="Maryanne" cell="27 72 555 0143" type="Roadside Emergency"
                     partner="Simah" elapsed={fmt(tick)} battery={62} />
        <RequestCard name="Johan Bekker" cell="27 81 555 0182" type="Security"
                     partner="Indwe" elapsed={fmt(tick + 47)} battery={18} urgent />
        <RequestCard name="Dr T. Naidoo" cell="27 83 555 0139" type="Medical"
                     partner="BornmanMarlow" elapsed={fmt(tick + 128)} battery={81} />
      </div>
      {/* Bottom-right status feed */}
      <div style={panel({ bottom: 20, right: 20, padding: 14, minWidth: 380 })} data-testid="ops-status-feed">
        <div style={{ fontSize: 11, color: C.slate, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Live status</div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, lineHeight: 1.7 }}>
          <div><span style={{ color: C.slate }}>14:32:04</span> · Maryanne · dispatched to CSG</div>
          <div><span style={{ color: C.slate }}>14:31:58</span> · Location resolved · Kirkwood, EC</div>
          <div><span style={{ color: C.slate }}>14:31:52</span> · <span style={{ color: C.red }}>PANIC</span> · Maryanne · Simah</div>
        </div>
      </div>
    </div>
  );
}
const panel = extra => ({
  position: "absolute", background: "rgba(255,255,255,0.88)", backdropFilter: "blur(16px)",
  border: "1px solid rgba(255,255,255,0.6)", borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", ...extra
});
const kbd = { background: C.navy, color: "#fff", padding: "3px 8px", borderRadius: 4, fontSize: 11, fontFamily: "'JetBrains Mono', monospace" };
const Stat = ({ n, label, color, mono }) => (
  <div>
    <div style={{ fontSize: 26, fontWeight: 700, color, letterSpacing: "-0.02em", fontFamily: mono ? "'JetBrains Mono', monospace" : undefined, fontVariantNumeric: "tabular-nums" }}>{n}</div>
    <div style={{ fontSize: 11, color: C.slate, marginTop: 2 }}>{label}</div>
  </div>
);
const RequestCard = ({ name, cell, type, partner, elapsed, battery, urgent }) => (
  <div data-testid={`request-card-${name.toLowerCase().replace(/ /g, "-")}`}
       style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(16px)", padding: 14, borderRadius: 10, border: `1px solid ${urgent ? C.red : "rgba(255,255,255,0.6)"}`, boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
      <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: "-0.01em" }}>{name.toUpperCase()}</div>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: urgent ? C.red : C.navy, fontVariantNumeric: "tabular-nums" }}>{elapsed}</div>
    </div>
    <div style={{ fontSize: 12, color: C.slate, fontFamily: "'JetBrains Mono', monospace" }}>{cell} · ResQ1 ({partner})</div>
    <div style={{ marginTop: 8, fontSize: 12, color: C.red, fontWeight: 600, letterSpacing: "0.03em" }}>▶ {type.toUpperCase()}</div>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
      <span style={{ fontSize: 11, color: C.slate }}>🔋</span>
      <div style={{ flex: 1, height: 6, background: "#eee", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${battery}%`, background: battery < 20 ? C.red : battery < 50 ? C.amber : C.green }} />
      </div>
      <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: C.slate, minWidth: 30 }}>{battery}%</span>
    </div>
    <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
      <button style={{ flex: 1, padding: "7px", background: C.green, color: "#fff", border: 0, borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Complete ✓</button>
      <button style={{ flex: 1, padding: "7px", background: C.navy, color: "#fff", border: 0, borderRadius: 5, fontSize: 12, fontWeight: 500, cursor: "pointer" }}>Details →</button>
    </div>
  </div>
);

/* ---------- 2. CONSUMER PWA HOME ---------- */
function PWAHome() {
  return (
    <div style={{ maxWidth: 400, margin: "20px auto", background: C.body, borderRadius: 24, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", padding: 24 }}
         data-testid="hero-pwa-home">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 32 }}>
        <img src="/resq1-logo.webp" alt="ResQ1 Emergency Protection" style={{ height: 56 }} />
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: C.ink }}>Good afternoon,</div>
      <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: C.ink, marginBottom: 14 }}>Gordon.</div>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", background: `${C.green}22`, color: C.green, borderRadius: 20, fontSize: 12, fontWeight: 600, marginBottom: 40 }}>
        <span style={{ width: 8, height: 8, background: C.green, borderRadius: "50%" }} /> System active · GPS locked
      </div>
      <button data-testid="pwa-panic-button"
              style={{ width: "100%", padding: "48px 20px", background: C.red, color: "#fff", border: 0, borderRadius: 16, fontSize: 28, fontWeight: 800, letterSpacing: "0.05em", cursor: "pointer", boxShadow: `0 12px 40px ${C.red}66`, marginBottom: 12 }}>
        HOLD TO PANIC
      </button>
      <div style={{ fontSize: 12, color: C.slate, textAlign: "center", marginBottom: 32 }}>Press and hold 3 seconds to arm</div>
      <button style={{ width: "100%", padding: 14, background: "transparent", color: C.navy, border: `1.5px solid ${C.navy}`, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 24 }}>
        Test my safety net
      </button>
      <div style={{ fontSize: 11, color: C.slate, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>My household · 4 of 6</div>
      {[
        { name: "Susan Bosman", role: "Spouse", ok: true },
        { name: "James Bosman", role: "Child · 14", ok: true },
        { name: "Anna Bosman", role: "Child · 11", ok: false },
      ].map(m => (
        <div key={m.name} style={{ display: "flex", alignItems: "center", padding: 12, background: "#fff", borderRadius: 8, marginBottom: 6, border: `1px solid #eee` }}>
          <div style={{ width: 32, height: 32, background: C.navy, color: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, marginRight: 12 }}>{m.name.split(" ").map(x => x[0]).join("")}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{m.name}</div>
            <div style={{ fontSize: 11, color: C.slate }}>{m.role}</div>
          </div>
          <div style={{ fontSize: 11, color: m.ok ? C.green : C.amber }}>{m.ok ? "● app active" : "● not installed"}</div>
        </div>
      ))}
    </div>
  );
}

/* ---------- 3. EMERGENCY ACTIVE ---------- */
function EmergencyActive() {
  return (
    <div style={{ maxWidth: 400, margin: "20px auto", background: "#2a0508", borderRadius: 24, overflow: "hidden", position: "relative", height: 780, boxShadow: `0 20px 80px ${C.red}66` }}
         data-testid="hero-emergency-active">
      <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 50% 40%, ${C.red}44 0%, transparent 60%)`, animation: "sos-pulse 2s ease-in-out infinite" }} />
      <style>{`@keyframes sos-pulse { 0%,100% { opacity: 0.5 } 50% { opacity: 1 } }`}</style>
      <div style={{ position: "relative", zIndex: 2, padding: 20, color: "#fff" }}>
        <button style={{ padding: "8px 14px", background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 6, fontSize: 12, cursor: "pointer" }}
                data-testid="sos-cancel-top">← Cancel panic</button>
        <div style={{ marginTop: 24, textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 16px", background: C.red, borderRadius: 20, fontSize: 12, fontWeight: 700, letterSpacing: "0.1em" }}>
            <span style={{ width: 8, height: 8, background: "#fff", borderRadius: "50%", animation: "sos-pulse 1s infinite" }} /> SOS ACTIVE · 00:42
          </div>
        </div>
        <div style={{ marginTop: 20, padding: 14, background: "rgba(0,0,0,0.4)", borderRadius: 10, backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ fontSize: 10, letterSpacing: "0.1em", opacity: 0.6, textTransform: "uppercase", marginBottom: 4 }}>Your location</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>7 Green Crescent, Highveld Estate</div>
          <div style={{ fontSize: 12, opacity: 0.7, fontFamily: "'JetBrains Mono', monospace" }}>-25.83386, 28.16592 · GPS ± 8m</div>
        </div>
        <div style={{ marginTop: 200, textAlign: "center" }}>
          <div style={{ fontSize: 11, opacity: 0.6, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>If the app fails, tap</div>
          <a href="tel:0664721844" data-testid="sos-fallback-call"
             style={{ display: "block", padding: "24px 20px", background: "#fff", color: C.red, borderRadius: 14, fontSize: 32, fontWeight: 800, letterSpacing: "-0.02em", fontFamily: "'JetBrains Mono', monospace", textDecoration: "none", boxShadow: "0 8px 30px rgba(0,0,0,0.4)" }}>
            📞 066 472 1844
          </a>
          <div style={{ fontSize: 11, opacity: 0.6, marginTop: 6 }}>CSG Control Room · 24/7</div>
        </div>
        <div style={{ position: "absolute", bottom: 20, left: 20, right: 20, padding: 12, background: "rgba(0,0,0,0.5)", borderRadius: 8, fontSize: 12, fontFamily: "'JetBrains Mono', monospace", borderLeft: `3px solid ${C.green}` }}>
          <div style={{ opacity: 0.7 }}>14:32:12 · Dispatched to CSG · <span style={{ color: C.green }}>ETA 4 min</span></div>
        </div>
      </div>
    </div>
  );
}

/* ---------- 4. INSTALL WALKTHROUGH ---------- */
function InstallWalkthrough() {
  return (
    <div style={{ maxWidth: 400, margin: "20px auto" }} data-testid="hero-install-walkthrough">
      <div style={{ background: "#fff", borderRadius: 24, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", border: `1px solid #eee` }}>
        <div style={{ background: C.navy, color: "#fff", padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 12, opacity: 0.7 }}>Install the ResQ1 app</div>
          <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace", opacity: 0.6 }}>iPhone · Safari</div>
        </div>
        <div style={{ padding: "20px 24px", background: C.body }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            {[1, 2, 3].map(n => (
              <div key={n} style={{ flex: 1, height: 4, borderRadius: 2, background: n === 1 ? C.red : "#ddd" }} />
            ))}
          </div>
          <div style={{ fontSize: 12, color: C.slate, marginBottom: 6, letterSpacing: "0.05em" }}>STEP 1 OF 3 · ABOUT 20 SECONDS</div>
          <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em", color: C.ink, marginBottom: 10, lineHeight: 1.2 }}>Tap the Share button at the bottom of your screen</div>
          <div style={{ fontSize: 14, color: C.slate, marginBottom: 20 }}>It{"'"}s the little box with an arrow pointing up.</div>
          <div style={{ background: "#f2f0eb", borderRadius: 14, padding: 20, position: "relative", marginBottom: 20, minHeight: 260 }}>
            <div style={{ background: "#fff", height: 180, borderRadius: 8, marginBottom: 10, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: "45%", left: "50%", transform: "translate(-50%,-50%)", opacity: 0.3, fontSize: 11, color: C.slate }}>your safari window</div>
            </div>
            <div style={{ background: "#fff", padding: "10px 12px", borderRadius: 8, display: "flex", justifyContent: "space-around", alignItems: "center", position: "relative" }}>
              <span style={{ fontSize: 20, opacity: 0.5 }}>◀</span>
              <span style={{ fontSize: 20, opacity: 0.5 }}>▶</span>
              <div style={{ position: "relative", padding: 4 }}>
                <span style={{ fontSize: 22 }}>⬆</span>
                <div style={{ position: "absolute", top: -8, left: -8, right: -8, bottom: -8, border: `3px solid ${C.red}`, borderRadius: "50%", animation: "sos-pulse 1.5s infinite" }} />
              </div>
              <span style={{ fontSize: 20, opacity: 0.5 }}>📖</span>
              <span style={{ fontSize: 20, opacity: 0.5 }}>▤</span>
            </div>
            <div style={{ position: "absolute", top: 200, right: 40, color: C.red, fontSize: 24, transform: "rotate(30deg)" }}>↓</div>
            <div style={{ position: "absolute", top: 175, right: 70, background: C.red, color: "#fff", padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 700, transform: "rotate(-4deg)" }}>Tap this!</div>
          </div>
          <button data-testid="install-next"
                  style={{ width: "100%", padding: 16, background: C.red, color: "#fff", border: 0, borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 10 }}>
            I tapped it — what{"'"}s next?
          </button>
          <button data-testid="install-help"
                  style={{ width: "100%", padding: 12, background: "transparent", color: C.slate, border: 0, fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>
            Nothing happened? Get help →
          </button>
        </div>
      </div>
    </div>
  );
}
